import * as React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import type {
  SegmentRow,
  SegmentVehicleItem,
} from 'app/pages/EmailsGridPage/apiService/emailsGridApiService';
import type { VehicleListKind } from 'app/pages/EmailsGridPage/hooks/useSegmentInventories';
import CopyToClipboard from 'app/components/CopyToClipboard';

export interface TestTemplatePreferencesSummaryProps {
  segmentRows: SegmentRow[];
  /** Current preferred VINs per segment+kind. Key format: `${segmentName}-${kind}`. */
  preferredVinsSnapshot: Record<string, string[]>;
  /** Cached vehicle details per segment+kind. Same key format as `preferredVinsSnapshot`. */
  vehiclesBySegmentKind: Record<string, SegmentVehicleItem[]>;
  loading?: boolean;
  error?: string | null;
  /** True while the consumer is batch-fetching missing segment inventories. */
  prefetchLoading?: boolean;
}

function buildKey(segmentName: string, kind: VehicleListKind) {
  return `${segmentName}-${kind}`;
}

function formatMoney(value: unknown): string {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `$${n.toLocaleString()}`;
}

function readBool(value: unknown): boolean {
  return (
    value === true ||
    value === 'true' ||
    value === 1 ||
    String(value ?? '').toLowerCase() === 'true'
  );
}

/** Compact read-only VIN card showing core vehicle details. Tight footprint for confirmation step. */
function VehicleSummaryCard({ vehicle }: { vehicle: SegmentVehicleItem }) {
  const isCpoCertified = readBool((vehicle as any).IsCertified);
  const daysInLotRaw =
    (vehicle as any).days_in_lot ??
    (vehicle as any).DaysInLot ??
    (vehicle as any).daysInLot;
  const daysInLot =
    daysInLotRaw !== undefined && daysInLotRaw !== null && !Number.isNaN(Number(daysInLotRaw))
      ? Number(daysInLotRaw)
      : null;

  const titleLine = [vehicle.Year, vehicle.Make, vehicle.Model]
    .filter((part) => part !== undefined && part !== null && String(part).trim() !== '')
    .join(' ');
  const msrpFormatted = formatMoney(vehicle.MSRP);
  const showMsrp = msrpFormatted !== '—';

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 1,
        bgcolor: 'background.paper',
        transition: 'border-color 0.15s ease',
        '&:hover': { borderColor: 'primary.light' },
      }}
    >
      <CardContent sx={{ p: 0.9, '&:last-child': { pb: 0.9 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mb: 0.4,
          }}
        >
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.7rem',
              wordBreak: 'break-all',
              lineHeight: 1.15,
              flex: 1,
              minWidth: 0,
            }}
            title={vehicle.VIN}
          >
            {vehicle.VIN}
          </Typography>
          <CopyToClipboard text={vehicle.VIN} size="small" />
          {isCpoCertified && (
            <Tooltip title="Certified Pre-Owned" arrow placement="top">
              <Chip
                label="CPO"
                size="small"
                color="success"
                sx={{
                  height: 14,
                  fontSize: '0.55rem',
                  fontWeight: 700,
                  '& .MuiChip-label': { px: 0.5 },
                }}
              />
            </Tooltip>
          )}
        </Box>

        {titleLine && (
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              fontSize: '0.74rem',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={titleLine}
          >
            {titleLine}
          </Typography>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 0.5,
            mt: 0.3,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: '0.66rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={vehicle.Trim || ''}
          >
            {vehicle.Trim || '—'}
          </Typography>
          {daysInLot != null && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.66rem', whiteSpace: 'nowrap' }}
            >
              {daysInLot}d
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 0.5,
            mt: 0.3,
          }}
        >
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ fontSize: '0.72rem', color: 'text.primary' }}
          >
            {formatMoney(vehicle.Price)}
          </Typography>
          {showMsrp && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.62rem' }}
            >
              MSRP {msrpFormatted}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

function VinSectionHeader({ label, count }: { label: string; count: number }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.6 }}>
      <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ fontSize: '0.74rem' }}>
        {label}
      </Typography>
      <Chip
        label={count}
        size="small"
        color={count > 0 ? 'primary' : 'default'}
        sx={{
          height: 16,
          fontSize: '0.62rem',
          fontWeight: 700,
          minWidth: 22,
          '& .MuiChip-label': { px: 0.6 },
        }}
      />
    </Box>
  );
}

function VehicleGrid({
  vins,
  vehicles,
}: {
  vins: string[];
  vehicles: SegmentVehicleItem[];
}) {
  const byVin = new Map(vehicles.map((v) => [v.VIN, v] as const));
  /** VINs still in preferences but not in current inventory — kept in data, hidden from UI. */
  const displayVins = vins.filter((vin) => byVin.has(vin));

  if (displayVins.length === 0) {
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontStyle: 'italic', display: 'block', py: 0.5, fontSize: '0.7rem' }}
      >
        No vehicles selected
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
        gap: 0.75,
      }}
    >
      {displayVins.map((vin) => (
        <VehicleSummaryCard key={vin} vehicle={byVin.get(vin)!} />
      ))}
    </Box>
  );
}

function countVinsInInventory(vins: string[], vehicles: SegmentVehicleItem[]): number {
  const vinSet = new Set(vehicles.map((v) => v.VIN));
  return vins.filter((vin) => vinSet.has(vin)).length;
}

/**
 * Read-only confirmation view shown on step 2 of `TestTemplateGenerationModal`.
 * Per-segment accordion — segments with selections are expanded by default, empty segments stay collapsed.
 */
export default function TestTemplatePreferencesSummary({
  segmentRows,
  preferredVinsSnapshot,
  vehiclesBySegmentKind,
  loading = false,
  error = null,
  prefetchLoading = false,
}: TestTemplatePreferencesSummaryProps) {
  const theme = useTheme();

  /** Per-segment expanded state — all collapsed by default; user expands segments to inspect VINs. */
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({});

  const handleToggle = React.useCallback(
    (segmentName: string) => (_e: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded((prev) => ({ ...prev, [segmentName]: isExpanded }));
    },
    [],
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 1.5 }}>
        {error}
      </Alert>
    );
  }

  if (!segmentRows.length) {
    return <Alert severity="info">No segments available for this dealer.</Alert>;
  }

  const totalSelected = segmentRows.reduce((sum, row) => {
    const n = preferredVinsSnapshot[buildKey(row.segment_name, 'new')]?.length ?? 0;
    const u = preferredVinsSnapshot[buildKey(row.segment_name, 'used')]?.length ?? 0;
    return sum + n + u;
  }, 0);
  const totalSegmentsWithSelections = segmentRows.filter((row) => {
    const n = preferredVinsSnapshot[buildKey(row.segment_name, 'new')]?.length ?? 0;
    const u = preferredVinsSnapshot[buildKey(row.segment_name, 'used')]?.length ?? 0;
    return n + u > 0;
  }).length;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        flex: 1,
        minHeight: 0,
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          px: 1.5,
          py: 0.75,
          borderRadius: 1,
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          borderColor: alpha(theme.palette.primary.main, 0.32),
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <DirectionsCarIcon sx={{ fontSize: 18 }} color="primary" />
            <Typography variant="body2" fontWeight={700} color="primary.dark">
              {totalSelected}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              VINs selected
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.6 }}>
            •
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" fontWeight={700} color="primary.dark">
              {totalSegmentsWithSelections}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              of {segmentRows.length} segments
            </Typography>
          </Box>
        </Box>
        {prefetchLoading && (
          <Box sx={{ mt: 0.5 }}>
            <LinearProgress sx={{ height: 3, borderRadius: 1 }} />
          </Box>
        )}
      </Paper>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          // Take whatever space the parent flex chain leaves; when too many segments /
          // expanded accordions overflow, this container itself scrolls instead of the modal.
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          pr: 0.5,
          // Force-visible scrollbar styling so the affordance is obvious on Windows 11
          // (which auto-hides native scrollbars) and any browser using overlay scrollbars.
          scrollbarGutter: 'stable',
          '&::-webkit-scrollbar': { width: 8 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(theme.palette.text.primary, 0.25),
            borderRadius: 4,
            '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.4) },
          },
          scrollbarColor: `${alpha(theme.palette.text.primary, 0.3)} transparent`,
          scrollbarWidth: 'thin',
        }}
      >
        {segmentRows.map((row) => {
          const newVins = preferredVinsSnapshot[buildKey(row.segment_name, 'new')] ?? [];
          const usedVins = preferredVinsSnapshot[buildKey(row.segment_name, 'used')] ?? [];
          const newVehicles = vehiclesBySegmentKind[buildKey(row.segment_name, 'new')] ?? [];
          const usedVehicles = vehiclesBySegmentKind[buildKey(row.segment_name, 'used')] ?? [];
          const newDisplayCount = countVinsInInventory(newVins, newVehicles);
          const usedDisplayCount = countVinsInInventory(usedVins, usedVehicles);
          const totalForSegment = newVins.length + usedVins.length;
          const isExpanded = !!expanded[row.segment_name];

          return (
            <Accordion
              key={row.segment_name}
              expanded={isExpanded}
              onChange={handleToggle(row.segment_name)}
              disableGutters
              elevation={0}
              square={false}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                opacity: totalForSegment === 0 ? 0.75 : 1,
                flexShrink: 0,
                '&:before': { display: 'none' },
                '&.Mui-expanded': { margin: 0 },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon fontSize="small" />}
                sx={{
                  minHeight: 40,
                  px: 1.5,
                  '& .MuiAccordionSummary-content': {
                    my: 0.5,
                    alignItems: 'center',
                    gap: 1,
                  },
                  '&.Mui-expanded': { minHeight: 40 },
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{ color: 'primary.dark', flex: 1 }}
                >
                  {row.segment_name}
                </Typography>
                <Chip
                  label={`${newDisplayCount} new`}
                  size="small"
                  color={newDisplayCount > 0 ? 'primary' : 'default'}
                  variant={newDisplayCount > 0 ? 'filled' : 'outlined'}
                  sx={{ height: 18, fontSize: '0.62rem', fontWeight: 600 }}
                />
                <Chip
                  label={`${usedDisplayCount} used`}
                  size="small"
                  color={usedDisplayCount > 0 ? 'primary' : 'default'}
                  variant={usedDisplayCount > 0 ? 'filled' : 'outlined'}
                  sx={{ height: 18, fontSize: '0.62rem', fontWeight: 600 }}
                />
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0.5, pb: 1, px: 1.5 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 1.25,
                  }}
                >
                  <Box>
                    <VinSectionHeader label="New Vehicles" count={newDisplayCount} />
                    <VehicleGrid vins={newVins} vehicles={newVehicles} />
                  </Box>
                  <Box>
                    <VinSectionHeader label="Used Vehicles" count={usedDisplayCount} />
                    <VehicleGrid vins={usedVins} vehicles={usedVehicles} />
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Box>
  );
}
